/**
 * AreasMap — interactive Leaflet map of every city we serve.
 *
 * - One blue pin per city, sized + colored by distance from office
 * - Highlighted teal pin for the office anchor with persistent label
 * - Click pin → navigate to that city's hero service page
 * - SEO: full plain-text city list rendered below the map so crawlers see
 *   linked anchors even before JS hydrates the map
 * - Works in EN and ES (lang prop swaps copy + deep links)
 *
 * Uses OpenStreetMap tiles → no API keys, no billing, no usage limits.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Patch default Leaflet marker icons (the bundled relative paths break under
// Webpack/Vite). We swap in inline CDN URLs for the office marker only —
// city pins use CircleMarker which doesn't need an icon.
const officeIcon = new L.Icon({
  iconUrl: 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const COPY = {
  en: {
    h2: 'Cities We Serve Within 30 Miles',
    subtitle: 'Click any pin to see services available for that city',
    office_label: 'Fort Worth Dental',
    services_label: 'services',
    distance_label: 'miles',
    view_link: 'View dental services →',
    all_cities: 'All cities we serve',
  },
  es: {
    h2: 'Ciudades que Atendemos dentro de 30 Millas',
    subtitle: 'Haz clic en cualquier marcador para ver los servicios disponibles',
    office_label: 'Fort Worth Dental',
    services_label: 'servicios',
    distance_label: 'millas',
    view_link: 'Ver servicios dentales →',
    all_cities: 'Todas las ciudades que atendemos',
  },
};

// Color band by distance — closer cities get stronger teal.
const pinColor = (miles) => {
  if (miles == null) return '#94a3b8';
  if (miles <= 10) return '#0f766e'; // teal-700
  if (miles <= 20) return '#0d9488'; // teal-600
  if (miles <= 30) return '#14b8a6'; // teal-500
  return '#5eead4'; // teal-300
};

const AreasMap = ({ lang = 'en' }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const t = COPY[lang] || COPY.en;

  useEffect(() => {
    fetch(`${API_URL}/api/service-areas/map-data?lang=${lang}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setData)
      .catch((e) => setError(String(e)));
  }, [lang]);

  if (error) {
    return (
      <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded">
        <p className="text-amber-800 text-sm">Map unavailable — see city list below.</p>
      </div>
    );
  }
  if (!data) {
    return <div className="h-96 bg-gray-100 rounded-lg animate-pulse" aria-label="Loading map…" />;
  }

  return (
    <section className="py-12 bg-white" data-testid={`areas-map-${lang}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">{t.h2}</h2>
          <p className="text-gray-600">{t.subtitle}</p>
          <p className="text-sm text-teal-700 font-semibold mt-2">
            {data.total_cities} {lang === 'es' ? 'ciudades' : 'cities'} · {data.total_services_covered}{' '}
            {t.services_label}
          </p>
        </div>

        <div
          className="rounded-2xl overflow-hidden shadow-xl border border-gray-200"
          style={{ height: '560px' }}
          data-testid="areas-map-leaflet"
        >
          <MapContainer
            center={[data.office.lat, data.office.lng]}
            zoom={9}
            scrollWheelZoom={false}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* Office anchor */}
            <Marker position={[data.office.lat, data.office.lng]} icon={officeIcon}>
              <Popup>
                <div className="text-sm">
                  <p className="font-bold text-teal-700 mb-1">🦷 {data.office.name}</p>
                  <p className="text-gray-700 mb-1">{data.office.address}</p>
                  <a href={`tel:${data.office.phone.replace(/[^0-9+]/g, '')}`} className="text-teal-600 font-medium">
                    {data.office.phone}
                  </a>
                </div>
              </Popup>
            </Marker>

            {/* City pins */}
            {data.cities.map((c) => (
              <CircleMarker
                key={c.slug}
                center={[c.lat, c.lng]}
                radius={Math.max(7, Math.min(14, Math.log10((c.population || 1000) + 1) * 2))}
                pathOptions={{ color: pinColor(c.distance_miles), fillColor: pinColor(c.distance_miles), fillOpacity: 0.75, weight: 1 }}
              >
                <Popup>
                  <div className="text-sm">
                    <p className="font-bold text-gray-900 mb-1">{c.name}</p>
                    <p className="text-gray-600 mb-1 text-xs">
                      {c.distance_miles} {t.distance_label} · {c.service_count} {t.services_label}
                      {c.population ? ` · ~${c.population.toLocaleString()} ${lang === 'es' ? 'residentes' : 'residents'}` : ''}
                    </p>
                    <Link to={c.deep_link} className="text-teal-600 font-medium hover:underline">
                      {t.view_link}
                    </Link>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>

        {/* SEO-friendly plain-text city list (always visible — crawlers see
            anchors immediately, no JS required) */}
        <div className="mt-8" data-testid="areas-map-city-list">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">{t.all_cities}</h3>
          <div className="flex flex-wrap gap-2 text-sm">
            {data.cities.map((c) => (
              <Link
                key={c.slug}
                to={c.deep_link}
                className="bg-teal-50 text-teal-700 px-3 py-1.5 rounded-full hover:bg-teal-100 transition"
              >
                {c.name} <span className="text-teal-500 text-xs">({c.distance_miles}mi)</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AreasMap;
